import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import { useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SocketProvider } from "@/context/SocketContext";
import { CallProvider } from "@/context/CallContext";
import SmoothScroll from "@/components/SmoothScroll";
import AgeGate from "@/components/AgeGate";
import Navbar from "@/components/Navbar";
import CherryBackdrop from "@/components/CherryBackdrop";
import MatchModal from "@/components/MatchModal";
import CallModal from "@/components/CallModal";
import SuperLikeToast from "@/components/SuperLikeToast";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Browse from "@/pages/Browse";
import Matches from "@/pages/Matches";
import ProfileDetail from "@/pages/ProfileDetail";
import EditProfile from "@/pages/EditProfile";
import Messages from "@/pages/Messages";
import Likes from "@/pages/Likes";
import Premium from "@/pages/Premium";
import Wallet from "@/pages/Wallet";
import Admin from "@/pages/Admin";
import ResetPassword from "@/pages/ResetPassword";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import Models from "@/pages/Models";
import LiveRoom from "@/pages/LiveRoom";
import CreatorRegister from "@/pages/CreatorRegister";
import Events from "@/pages/Events";
import Support from "@/pages/Support";
import Verify from "@/pages/Verify";
import CreatorLink from "@/pages/CreatorLink";
import EmailConfirm from "@/pages/EmailConfirm";
import Hub from "@/pages/Hub";
import { InstallPrompt } from "@/components/InstallPrompt";
import { MobileTabBar } from "@/components/MobileTabBar";
import SignIns from "@/pages/SignIns";
import Apply from "@/pages/Apply";
import JoinCreator from "@/pages/JoinCreator";
import JoinMember from "@/pages/JoinMember";
import { captureSource, loadMarketing, takeSource, fireSignupConversion } from "@/lib/marketing";

function TagRoute() {
  const { tag } = useParams();
  return /^[a-z0-9][a-z0-9_.]{2,23}@velluto$/i.test(tag || "") ? <CreatorLink /> : <Navigate to="/" replace />;
}
import { isVerified } from "@/lib/verification";

function Protected({ children, verified = false }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="pt-32 text-center text-text-muted">Loading…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (user.email_verified === false && loc.pathname !== "/confirm-email") return <Navigate to="/confirm-email" replace />;
  if (verified && !isVerified(user)) return <Navigate to="/verify" replace state={{ from: loc.pathname }} />;
  return children;
}

// Handles the return trip from Emergent Google OAuth ({app}/#session_id=...).
function AuthCallback() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sid, ref: localStorage.getItem("velluto_ref") || undefined, invite: localStorage.getItem("velluto_invite") || undefined, source: takeSource() });
        localStorage.removeItem("velluto_ref");
        localStorage.removeItem("velluto_invite");
        if (data.created_at && Date.now() - new Date(data.created_at).getTime() < 120000) fireSignupConversion();
        setUser(data);
        const next = sessionStorage.getItem("velluto_next"); sessionStorage.removeItem("velluto_next");
        const dest = next || (!data.age || !data.photo_url ? "/me" : "/hub");
        window.history.replaceState({}, "", dest);
        nav(dest, { replace: true });
        toast.success(`Welcome, ${data.display_name}`);
      } catch {
        window.history.replaceState({}, "", "/auth");
        nav("/auth", { replace: true });
        toast.error("Google sign-in failed. Please try again.");
      }
    })();
  }, [nav, setUser]);
  return <div className="pt-32 text-center text-text-muted">Signing you in…</div>;
}

function AppRoutes() {
  if (typeof window !== "undefined" && window.location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/c/:handle" element={<CreatorLink />} />
      <Route path="/verify-email" element={<EmailConfirm />} />
      <Route path="/confirm-email-change" element={<EmailConfirm />} />
      <Route path="/confirm-email" element={<Protected><EmailConfirm /></Protected>} />
      <Route path="/:tag" element={<TagRoute />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="/browse" element={<Protected verified><Browse /></Protected>} />
      <Route path="/matches" element={<Protected verified><Matches /></Protected>} />
      <Route path="/profile/:id" element={<Protected verified><ProfileDetail /></Protected>} />
      <Route path="/me" element={<Protected><EditProfile /></Protected>} />
      <Route path="/messages" element={<Protected verified><Messages /></Protected>} />
      <Route path="/messages/:id" element={<Protected verified><Messages /></Protected>} />
      <Route path="/likes" element={<Protected verified><Likes /></Protected>} />
      <Route path="/premium" element={<Protected><Premium /></Protected>} />
      <Route path="/wallet" element={<Protected><Wallet /></Protected>} />
      <Route path="/models" element={<Protected verified><Models /></Protected>} />
      <Route path="/models/:id/live" element={<Protected verified><LiveRoom /></Protected>} />
      <Route path="/creator/register" element={<Protected verified><CreatorRegister /></Protected>} />
      <Route path="/events" element={<Protected><Events /></Protected>} />
      <Route path="/support" element={<Protected><Support /></Protected>} />
      <Route path="/verify" element={<Protected><Verify /></Protected>} />
      <Route path="/hub" element={<Protected><Hub /></Protected>} />
      <Route path="/security/sign-ins" element={<Protected><SignIns /></Protected>} />
      <Route path="/apply" element={<Apply />} />
      <Route path="/join" element={<JoinCreator />} />
      <Route path="/vip" element={<JoinMember />} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
    </Routes>
  );
}

function Home() {
  const { user } = useAuth();
  if (user) return <Navigate to="/hub" replace />;
  return <Landing />;
}

function App() {
  useEffect(() => { captureSource(); loadMarketing(); }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SocketProvider>
            <CallProvider>
              <SmoothScroll>
                <AgeGate>
                  <CherryBackdrop />
                  <Navbar />
                  <MatchModal />
                  <CallModal />
                  <SuperLikeToast />
                  <InstallPrompt />
                  <AppRoutes />
                  <MobileTabBar />
                </AgeGate>
              </SmoothScroll>
            </CallProvider>
          </SocketProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
